import type {ExportOptions, ExportResponse} from "@hosttools/core/utils/exportReservations";
import type {Endpoints} from "@hosttools/web/server/routes/routes.reservations.types";
import type {AxiosRequestConfig} from "axios";
import type {PaginateResult} from "mongoose";
import qs from "qs";

import type {ReservationPost, ReservationRaw} from "../../models/reservation";
import {Reservation} from "../../models/reservation";
import {downloadFileBlob} from "../../utils/file";
import {Base} from "../base";

export interface ReservationPostPayload {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    preferredLocale: string;
    listingID: string;
    startDate: string;
    endDate: string;
    reservationID?: string;
    checkInTime: number;
    checkOutTime: number;
    notes?: string;
    hostPayout?: number;
    guestPrice?: number;
}

export interface ReservationsSearchOptions {
    unread?: 1 | 0;
    keyword?: string;
    offset: number;
    limit: number;
    sortBy: string;
    demo?: boolean;
}

class ReservationService extends Base<Endpoints> {
    async fetchReservation(reservationID: string, demo?: boolean) {
        const url = `/reservation/${reservationID}${demo ? "?demo=true" : ""}`;
        const {data} = await this.http.get<ReservationRaw>(url);
        return new Reservation(data);
    }

    async fetchReservations(
        filter?: ReservationsSearchOptions,
        config?: AxiosRequestConfig
    ): Promise<PaginateResult<Reservation>> {
        // skip empty string, not sure if there is a better way to handle this
        if (typeof filter?.keyword === "string" && !filter.keyword) {
            delete filter.keyword;
        }
        const strFilters = filter ? `${qs.stringify(filter, {skipNulls: true})}` : "";
        const url = `/reservations${strFilters ? `?${strFilters}` : ""}`;
        const {data} = await this.http.get<PaginateResult<ReservationRaw>>(url, config);

        return {
            ...data,
            docs: data.docs.map(elem => new Reservation(elem))
        };
    }

    async fetchReservationPosts(
        reservationID: string,
        params: {
            limit: number;
            offset: number;
            demo?: boolean;
        },
        config?: AxiosRequestConfig
    ) {
        const {data} = await this.http.get<PaginateResult<ReservationPost>>(
            `/reservation/posts/${reservationID}?${qs.stringify(params, {skipNulls: true})}`,
            config
        );
        return data;
    }

    async fetchThread(
        reservationID: string,
        nextPageID?: string,
        config?: AxiosRequestConfig,
        demo?: boolean
    ) {
        const queryString = qs.stringify({
            nextPageID,
            demo
        });

        const {data} = await this.http.get(
            `/getThread/${reservationID}${queryString ? `?${queryString}` : ""}`,
            config
        );

        return data;
    }

    async updatePosts(reservationID: string) {
        const url = `/updatePosts/${reservationID}`;
        const res = await this.http.get(url);

        return res.data;
    }

    async setRead(reservationID: string) {
        await this.http.post(`/setRead/${reservationID}`);
    }

    async cancelReservation(reservationID: string) {
        const url = "/cancelReservation";

        const {data} = await this.http.post(url, {
            reservationID
        });

        return data;
    }

    async fetchReservationExport(params?: ExportOptions, isDemo?: boolean) {
        const queryString = qs.stringify({
            ...params,
            demo: isDemo ? "true" : undefined
        });
        const url = `/reservationExport${queryString ? `?${queryString}` : ""}`;
        const res = await this.http.get<PaginateResult<ExportResponse>>(url);

        return res.data;
    }

    async fetchReservationExportDownload(params?: ExportOptions) {
        const url = `/reservationExport/download${params ? `?${qs.stringify(params)}` : ""}`;
        const res = await this.http.get<Blob>(url, {responseType: "blob"});
        const contentDisposition = res.headers["content-disposition"];

        let fileName = "exportReservation.csv";

        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
            if (fileNameMatch?.length === 2) {
                [, fileName] = fileNameMatch;
            }
        }

        downloadFileBlob(res.data, fileName);
    }
}

export default ReservationService;
